/**
 * Motor de cálculo Cotizador Factoring (espejo de bi_mgc_cotizador_utils.py)
 * Soporta N facturas por cotización.
 */

export function defaultParametros() {
  return {
    costo_fondo_mensual: 0.00748,
    spread_minimo: 0.01,
    contrato_notarial: 115000,
    contrato_digital: 65000,
    notificacion_por_deudor: 7500,
    notificacion_facturas_por_cobro: 12,
    costo_operacion_hasta_5mm: 25000,
    costo_operacion_sobre_5mm: 38000,
    cesion_realizada: 17000,
    tramos_cobro: [
      { desde: 0, hasta: 2000000, comision: 10000, gastos: 10000 },
      { desde: 2000001, hasta: 5000000, comision: 25000, gastos: 15000 },
      { desde: 5000001, hasta: 15000000, comision: 50000, gastos: 25000 },
      { desde: 15000001, hasta: 30000000, comision: 90000, gastos: 35000 },
      { desde: 30000001, hasta: 999999999, comision: 0, gastos: 0 },
    ],
    reglas_pre_eval: {
      plazo_minimo: 15,
      plazo_maximo: 70,
      monto_max_operacion: 50000000,
      monto_max_factura: 30000000,
      concentracion_max_deudor: 30000000,
    },
  };
}

export function defaultReglasPricing() {
  const manual = 'Revision manual';
  const auto = 'Automatico';
  const pendiente = 'Pendiente validar';
  const rows = [
    ['A1', pendiente, 0, manual], ['A2', pendiente, 0, manual],
    ['A3', pendiente, 0, manual], ['A4', pendiente, 0, manual],
    ['B11', pendiente, 0, manual], ['B12', pendiente, 0, manual], ['B13', pendiente, 0, manual],
    ['C', pendiente, 0, manual], ['D', pendiente, 0, manual],
    ['E1', pendiente, 0, manual], ['E2', pendiente, 0, manual], ['E3', pendiente, 0, manual],
    ['E4', pendiente, 0, manual], ['E5', pendiente, 0, manual], ['E6', pendiente, 0, manual],
    ['T13', 'Preferente', 0, auto], ['T12', 'Preferente', 0, auto], ['T11', 'Preferente', 0, auto],
    ['T10', 'Estandar', 0.001, auto], ['T9', 'Estandar', 0.001, auto],
    ['T8', 'Monitoreo', 0.002, auto], ['T7', 'Monitoreo', 0.002, auto],
    ['T6', 'Precaucion', 0.003, auto], ['T5', 'Precaucion', 0.003, auto],
    ['T4', 'Revision', 0.005, manual],
  ];
  return rows.map(([clasificacion, banda_pricing, ajuste_tasa_mensual, flujo]) => ({
    clasificacion, banda_pricing, ajuste_tasa_mensual, flujo, comentario: '',
  }));
}

export function emptyDocumento(overrides = {}) {
  return {
    folio: '',
    rut_deudor: '',
    clasificacion_bd: '',
    clasificacion_aplicada: '',
    clasificacion_manual: false,
    monto_factura: 0,
    monto_operacion: 0,
    plazo: 0,
    comision_real: null,
    gastos_real: null,
    ...overrides,
  };
}

export function normalizarDocumentos(quote) {
  if (Array.isArray(quote?.documentos) && quote.documentos.length > 0) {
    return quote.documentos.map((d, i) => ({ ...emptyDocumento(), ...d, _idx: i }));
  }
  return [emptyDocumento({
    folio: quote.folio || '',
    rut_deudor: quote.rut_deudor || '',
    clasificacion_bd: quote.clasificacion_bd || '',
    clasificacion_aplicada: quote.clasificacion_aplicada || quote.clasificacion_deudor || '',
    clasificacion_manual: !!quote.clasificacion_manual,
    monto_factura: Number(quote.monto_factura) || 0,
    monto_operacion: Number(quote.monto_operacion ?? quote.monto_anticipo ?? quote.monto_factura) || 0,
    plazo: Number(quote.plazo) || 0,
    comision_real: quote.comision_real != null ? quote.comision_real : null,
    gastos_real: quote.gastos_real != null ? quote.gastos_real : null,
    _idx: 0,
  })];
}

function normSiNo(val) {
  const s = String(val || '').trim().toLowerCase();
  if (['si', 'sí', 's', 'yes', '1', 'true'].includes(s)) return 'si';
  return 'no';
}

function rutKey(rut) {
  return String(rut || '').trim().toUpperCase().replace(/\./g, '').replace(/\s/g, '') || '';
}

function tramoCobro(monto, tramos) {
  const m = Number(monto) || 0;
  for (const t of tramos) {
    if (t.desde <= m && m <= t.hasta) {
      return { comision: Number(t.comision), gastos: Number(t.gastos), desde: t.desde, hasta: t.hasta };
    }
  }
  return { comision: 0, gastos: 0, desde: 0, hasta: 0 };
}

function contratoCobradoCliente(tipoCliente, contratoVigente, tipoContrato, digital, notarial) {
  const tc = String(tipoCliente || '').trim().toLowerCase();
  const cv = normSiNo(contratoVigente);
  const tcon = String(tipoContrato || '').trim().toLowerCase();
  if (tc === 'nuevo' && cv === 'no') {
    if (tcon.includes('digital')) return digital;
    if (tcon.includes('notarial')) return notarial;
  }
  return 0;
}

function costoContratoInterno(contratoVigente, tipoContrato, params) {
  const cv = normSiNo(contratoVigente);
  const tcon = String(tipoContrato || '').trim().toLowerCase();
  if (cv === 'no') {
    if (tcon.includes('digital')) return Number(params.contrato_digital);
    if (tcon.includes('notarial')) return Number(params.contrato_notarial);
  }
  return 0;
}

function isFlujoAuto(flujo) {
  return String(flujo || '').toLowerCase().replace('ó', 'o').startsWith('automat');
}

export function lookupReglaPricing(clasificacion, reglas) {
  const code = String(clasificacion || '').trim().toUpperCase();
  const list = Array.isArray(reglas) ? reglas : [];
  return list.find((r) => r && String(r.clasificacion).trim().toUpperCase() === code) || null;
}

const reglaPricingFallback = (clasificacion = '') => ({
  clasificacion: clasificacion || '',
  banda_pricing: 'Sin regla',
  ajuste_tasa_mensual: 0,
  flujo: 'Revision manual',
  comentario: '',
});

export function calcularConcentracion(documentos) {
  const byRut = {};
  documentos.forEach((d, i) => {
    const k = rutKey(d.rut_deudor) || `__line_${i}`;
    byRut[k] = (byRut[k] || 0) + (Number(d.monto_operacion) || 0);
  });
  const vals = Object.values(byRut);
  return vals.length ? Math.max(...vals) : 0;
}

export function cantidadDeudoresDistintos(documentos) {
  const keys = new Set();
  documentos.forEach((d, i) => {
    const k = rutKey(d.rut_deudor);
    if (k) keys.add(k);
    else keys.add(`__line_${i}`);
  });
  return Math.max(1, keys.size);
}

export function calcularNotificacionDeudores(documentos, params) {
  const montoCobro = Number(params.notificacion_por_deudor) || 7500;
  const umbral = Math.max(1, parseInt(params.notificacion_facturas_por_cobro, 10) || 12);
  const byRut = {};
  documentos.forEach((d, i) => {
    const k = rutKey(d.rut_deudor) || `__sin_rut_${i}`;
    byRut[k] = (byRut[k] || 0) + 1;
  });
  const desglose = [];
  let total = 0;
  Object.entries(byRut).forEach(([k, facturas]) => {
    const cobros = Math.ceil(facturas / umbral);
    const monto = cobros * montoCobro;
    total += monto;
    desglose.push({
      rut: k.startsWith('__') ? '(sin RUT)' : k,
      facturas,
      cobros,
      monto,
    });
  });
  return { total, desglose, monto_por_cobro: montoCobro, facturas_por_cobro: umbral };
}

export function calcularLinea(documento, cabecera, params, reglasList, pre) {
  const p = params;
  const spreadMin = Number(p.spread_minimo);
  const costoFondo = Number(p.costo_fondo_mensual);
  const tasaReal = Number(cabecera.tasa_operacion_real) || 0;
  const tasaBase = Number(cabecera.tasa_objetivo_base ?? cabecera.tasa_operacion_real) || 0;

  const montoFactura = Number(documento.monto_factura) || 0;
  const montoOperacion = Number(documento.monto_operacion) || montoFactura;
  const plazo = Number(documento.plazo) || 0;

  const plazoMin = Number(pre.plazo_minimo ?? 15);
  const plazoMax = Number(pre.plazo_maximo ?? 70);
  const maxFact = Number(pre.monto_max_factura ?? 30_000_000);

  const clasificacion = String(documento.clasificacion_aplicada || documento.clasificacion_bd || '').trim();
  const alertas = [];
  let regla = lookupReglaPricing(clasificacion, reglasList);

  if (!clasificacion) {
    alertas.push(`Factura ${documento.folio || documento._idx + 1}: sin clasificación de deudor.`);
    regla = reglaPricingFallback();
  } else if (!regla) {
    alertas.push(`Factura ${documento.folio || documento._idx + 1}: clasificación ${clasificacion} sin regla de pricing.`);
    regla = reglaPricingFallback(clasificacion);
  }

  const ajuste = Number(regla.ajuste_tasa_mensual) || 0;
  const flujo = String(regla.flujo || 'Revision manual');
  const banda = String(regla.banda_pricing || '');
  const tasaPoliticaFinal = Math.max(spreadMin, tasaBase + ajuste);

  const tramo = tramoCobro(montoOperacion, p.tramos_cobro);
  const comisionPolitica = tramo.comision;
  const gastosPolitica = tramo.gastos;
  const comisionReal = documento.comision_real != null && documento.comision_real !== ''
    ? Number(documento.comision_real) : tramo.comision;
  const gastosReal = documento.gastos_real != null && documento.gastos_real !== ''
    ? Number(documento.gastos_real) : tramo.gastos;

  const interesReal = montoOperacion * tasaReal * plazo / 30;
  const interesPolitica = montoOperacion * tasaPoliticaFinal * plazo / 30;
  const ingresoBrutoReal = interesReal + comisionReal + gastosReal;
  const ingresoBrutoPolitica = interesPolitica + comisionPolitica + gastosPolitica;
  const ingresoMinimoSpread = montoOperacion * (costoFondo + spreadMin) * plazo / 30;
  const costoFondoPesos = montoOperacion * costoFondo * plazo / 30;

  return {
    folio: documento.folio,
    rut_deudor: documento.rut_deudor,
    clasificacion_aplicada: clasificacion,
    monto_factura: montoFactura,
    monto_operacion: montoOperacion,
    plazo,
    alertas,
    clasificacion: { codigo: clasificacion, banda, ajuste_tasa_mensual: ajuste, flujo, tasa_objetivo_final: tasaPoliticaFinal },
    tramo: { desde: tramo.desde, hasta: tramo.hasta, comision: comisionPolitica, gastos: gastosPolitica },
    real: {
      interes: interesReal,
      comision: comisionReal,
      gastos: gastosReal,
      ingreso_bruto: ingresoBrutoReal,
      ingreso_minimo_spread: ingresoMinimoSpread,
      costo_fondo: costoFondoPesos,
    },
    politica: {
      interes: interesPolitica,
      comision: comisionPolitica,
      gastos: gastosPolitica,
      ingreso_bruto: ingresoBrutoPolitica,
      ingreso_minimo_spread: montoOperacion * (costoFondo + spreadMin) * plazo / 30,
    },
    controles_linea: {
      plazo: plazo >= plazoMin && plazo <= plazoMax,
      monto_factura: montoFactura <= maxFact,
      clasificacion_flujo: isFlujoAuto(flujo),
    },
  };
}

function peorClasificacionResumen(lineas) {
  const manual = lineas.find((l) => !l.controles_linea.clasificacion_flujo);
  if (manual) return manual.clasificacion;
  return lineas[0]?.clasificacion || { codigo: '', banda: '', ajuste_tasa_mensual: 0, flujo: 'Revision manual', tasa_objetivo_final: 0 };
}

function armarReglasAplicadas(ctx) {
  const {
    lineas, cabecera, params, notif, costoOp, costoOpLabel,
    contratoPolitica, contratoReal, costoContratoInternoPesos,
    cesion, costoFondoTotal, pre, controles, concentracion,
    montoOperacionTotal, plazoPonderado, tasaReal, tasaBase,
  } = ctx;
  const reglas = [];

  reglas.push({
    categoria: 'Tasa',
    descripcion: 'Tasa operación real (cotización)',
    formula: `${(tasaReal * 100).toFixed(3)}% mensual`,
    monto: null,
    aplica_a: 'Cotización',
  });
  reglas.push({
    categoria: 'Tasa',
    descripcion: 'Tasa objetivo base (cotización)',
    formula: `${(tasaBase * 100).toFixed(3)}% mensual`,
    monto: null,
    aplica_a: 'Cotización',
  });

  lineas.forEach((l, i) => {
    const label = l.folio || `Factura ${i + 1}`;
    reglas.push({
      categoria: 'Tramo cobro',
      descripcion: `Comisión/gastos — ${label}`,
      formula: `Anticipo ${l.monto_operacion.toLocaleString('es-CL')} → tramo $${l.tramo.desde.toLocaleString('es-CL')}-$${l.tramo.hasta.toLocaleString('es-CL')}`,
      monto: l.real.comision + l.real.gastos,
      aplica_a: label,
    });
    reglas.push({
      categoria: 'Interés',
      descripcion: `Interés real — ${label}`,
      formula: `${l.monto_operacion.toLocaleString('es-CL')} × ${(tasaReal * 100).toFixed(3)}% × ${l.plazo}/30`,
      monto: l.real.interes,
      aplica_a: label,
    });
    reglas.push({
      categoria: 'Clasificación',
      descripcion: `Deudor ${l.clasificacion_aplicada || '—'} — ${label}`,
      formula: `Flujo: ${l.clasificacion.flujo}`,
      monto: null,
      aplica_a: label,
    });
  });

  if (contratoPolitica > 0 || contratoReal > 0) {
    reglas.push({
      categoria: 'Contrato',
      descripcion: 'Contrato cobrado al cliente',
      formula: `${cabecera.tipo_cliente} / ${cabecera.tipo_contrato}`,
      monto: contratoReal || contratoPolitica,
      aplica_a: 'Cotización',
    });
  }

  reglas.push({
    categoria: 'Costo operación',
    descripcion: 'Costo fijo operación',
    formula: costoOpLabel,
    monto: costoOp,
    aplica_a: 'Cotización',
  });

  notif.desglose.forEach((d) => {
    reglas.push({
      categoria: 'Notificación deudor',
      descripcion: `Deudor ${d.rut}`,
      formula: `${d.facturas} factura(s) → ${d.cobros} cobro(s) × $${notif.monto_por_cobro.toLocaleString('es-CL')} (cada ${notif.facturas_por_cobro} facturas)`,
      monto: d.monto,
      aplica_a: d.rut,
    });
  });

  reglas.push({
    categoria: 'Cesión',
    descripcion: 'Cesión realizada',
    formula: 'Una vez por cotización',
    monto: cesion,
    aplica_a: 'Cotización',
  });

  reglas.push({
    categoria: 'Costo fondo',
    descripcion: 'Costo de fondo (suma por factura)',
    formula: `Plazo ponderado: ${plazoPonderado.toFixed(1)} días`,
    monto: costoFondoTotal,
    aplica_a: 'Cotización',
  });

  if (costoContratoInternoPesos > 0) {
    reglas.push({
      categoria: 'Costo contrato',
      descripcion: 'Costo contrato interno',
      formula: cabecera.tipo_contrato,
      monto: costoContratoInternoPesos,
      aplica_a: 'Cotización',
    });
  }

  const maxOp = Number(pre.monto_max_operacion ?? 50_000_000);
  const maxConc = Number(pre.concentracion_max_deudor ?? 30_000_000);
  const spreadMin = Number(params.spread_minimo);

  reglas.push({
    categoria: 'Pre-evaluación',
    descripcion: 'Monto operación total',
    formula: `<= $${maxOp.toLocaleString('es-CL')}`,
    monto: montoOperacionTotal,
    aplica_a: controles.monto_operacion ? 'Cumple' : 'No cumple',
  });
  reglas.push({
    categoria: 'Pre-evaluación',
    descripcion: 'Concentración deudor',
    formula: `<= $${maxConc.toLocaleString('es-CL')}`,
    monto: concentracion,
    aplica_a: controles.concentracion ? 'Cumple' : 'No cumple',
  });
  reglas.push({
    categoria: 'Pre-evaluación',
    descripcion: 'Spread mínimo',
    formula: `>= ${(spreadMin * 100).toFixed(2)}%`,
    monto: null,
    aplica_a: controles.spread_real ? 'Cumple' : 'No cumple',
  });

  return reglas;
}

export function calcularCotizacion(quote, params = defaultParametros(), reglasPricing = defaultReglasPricing()) {
  const p = { ...defaultParametros(), ...(params || {}) };
  const reglasList = Array.isArray(reglasPricing) && reglasPricing.length
    ? reglasPricing
    : defaultReglasPricing();
  const pre = p.reglas_pre_eval || {};
  const documentos = normalizarDocumentos(quote);

  const cabecera = {
    tipo_cliente: quote.tipo_cliente,
    contrato_vigente: quote.contrato_vigente,
    tipo_contrato: quote.tipo_contrato,
    tasa_operacion_real: Number(quote.tasa_operacion_real) || 0,
    tasa_objetivo_base: Number(quote.tasa_objetivo_base ?? quote.tasa_operacion_real) || 0,
    descuento_real: Number(quote.descuento_real) || 0,
    descuento_campana: Number(quote.descuento_campana) || 0,
    descuento_ejecutivo: Number(quote.descuento_ejecutivo) || 0,
    contrato_real: Number(quote.contrato_real) || 0,
  };

  const lineas = documentos.map((doc) => calcularLinea(doc, cabecera, p, reglasList, pre));
  const alertas = lineas.flatMap((l) => l.alertas);

  const montoFacturaTotal = lineas.reduce((s, l) => s + l.monto_factura, 0);
  const montoOperacionTotal = lineas.reduce((s, l) => s + l.monto_operacion, 0);
  const concentracion = calcularConcentracion(documentos);
  const cantidadDeudores = cantidadDeudoresDistintos(documentos);
  const plazoPonderado = montoOperacionTotal > 0
    ? lineas.reduce((s, l) => s + l.monto_operacion * l.plazo, 0) / montoOperacionTotal
    : 0;

  const sumReal = (field) => lineas.reduce((s, l) => s + l.real[field], 0);
  const sumPol = (field) => lineas.reduce((s, l) => s + l.politica[field], 0);

  const costoFondo = Number(p.costo_fondo_mensual);
  const spreadMin = Number(p.spread_minimo);
  const contratoPolitica = contratoCobradoCliente(
    quote.tipo_cliente, quote.contrato_vigente, quote.tipo_contrato,
    Number(p.contrato_digital), Number(p.contrato_notarial),
  );
  const contratoReal = cabecera.contrato_real || contratoPolitica;

  const interesReal = sumReal('interes');
  const comisionReal = sumReal('comision');
  const gastosReal = sumReal('gastos');
  const interesPolitica = sumPol('interes');
  const comisionPolitica = sumPol('comision');
  const gastosPolitica = sumPol('gastos');

  const ingresoBrutoReal = interesReal + comisionReal + gastosReal + contratoReal;
  const ingresoBrutoPolitica = interesPolitica + comisionPolitica + gastosPolitica + contratoPolitica;

  const ingresoMinimoSpread = lineas.reduce((s, l) => s + l.real.ingreso_minimo_spread, 0);
  const ingresoMinimoSpreadPol = lineas.reduce((s, l) => s + l.politica.ingreso_minimo_spread, 0);
  const capacidadDescuentoReal = Math.max(0, ingresoBrutoReal - ingresoMinimoSpread);
  const capacidadDescuentoPolitica = Math.max(0, ingresoBrutoPolitica - ingresoMinimoSpreadPol);

  const descuentoAplicadoPolitica = Math.min(
    cabecera.descuento_campana + cabecera.descuento_ejecutivo,
    capacidadDescuentoPolitica,
  );

  const ingresoNetoReal = ingresoBrutoReal - cabecera.descuento_real;
  const ingresoNetoPolitica = ingresoBrutoPolitica - descuentoAplicadoPolitica;
  const montoLiquidoReal = montoOperacionTotal - ingresoNetoReal;
  const montoLiquidoPolitica = montoOperacionTotal - ingresoNetoPolitica;

  const costoFondoPesos = sumReal('costo_fondo');
  const costoContratoInternoPesos = costoContratoInterno(quote.contrato_vigente, quote.tipo_contrato, p);
  const costoOp = montoOperacionTotal <= 5_000_000
    ? Number(p.costo_operacion_hasta_5mm)
    : Number(p.costo_operacion_sobre_5mm);
  const costoOpLabel = montoOperacionTotal <= 5_000_000
    ? 'Operación total ≤ $5MM'
    : 'Operación total > $5MM';
  const notif = calcularNotificacionDeudores(documentos, p);
  const cesion = Number(p.cesion_realizada);
  const costoOperacional = costoOp + notif.total + cesion;
  const costoInternoTotal = costoFondoPesos + costoContratoInternoPesos + costoOperacional;

  const margenReal = ingresoNetoReal - costoInternoTotal;
  const margenPolitica = ingresoNetoPolitica - costoInternoTotal;

  const tasaIntegral = (ingresoNeto) => (
    montoOperacionTotal === 0 || plazoPonderado === 0 ? 0 : ingresoNeto / montoOperacionTotal * 30 / plazoPonderado
  );
  const tasaIntReal = tasaIntegral(ingresoNetoReal);
  const tasaIntPolitica = tasaIntegral(ingresoNetoPolitica);
  const spreadReal = tasaIntReal - costoFondo;
  const spreadPolitica = tasaIntPolitica - costoFondo;

  const plazoMin = Number(pre.plazo_minimo ?? 15);
  const plazoMax = Number(pre.plazo_maximo ?? 70);
  const maxOp = Number(pre.monto_max_operacion ?? 50_000_000);
  const maxConc = Number(pre.concentracion_max_deudor ?? 30_000_000);

  const todasLineasPlazo = lineas.every((l) => l.controles_linea.plazo);
  const todasLineasFactura = lineas.every((l) => l.controles_linea.monto_factura);
  const todasLineasFlujo = lineas.every((l) => l.controles_linea.clasificacion_flujo);
  const clasResumen = peorClasificacionResumen(lineas);

  const controles = {
    plazo: todasLineasPlazo,
    monto_operacion: montoOperacionTotal <= maxOp,
    monto_factura: todasLineasFactura,
    concentracion: concentracion <= maxConc,
    spread_real: spreadReal >= spreadMin,
    spread_politica: spreadPolitica >= spreadMin,
    clasificacion_flujo: todasLineasFlujo,
  };

  const cumplePreEval = Object.values(controles).every(Boolean);
  const detallePreEval = [
    { regla: 'Plazo (todas las facturas)', ok: controles.plazo, criterio: `${plazoMin}-${plazoMax} días`, valor: plazoPonderado },
    { regla: 'Monto operación total', ok: controles.monto_operacion, criterio: `<= $${maxOp.toLocaleString('es-CL')}`, valor: montoOperacionTotal },
    { regla: 'Monto factura (cada línea)', ok: controles.monto_factura, criterio: `<= $${Number(pre.monto_max_factura ?? 30_000_000).toLocaleString('es-CL')}`, valor: montoFacturaTotal },
    { regla: 'Concentración deudor', ok: controles.concentracion, criterio: `<= $${maxConc.toLocaleString('es-CL')}`, valor: concentracion },
    { regla: 'Spread real', ok: controles.spread_real, criterio: `>= ${(spreadMin * 100).toFixed(2)}%`, valor: spreadReal },
    { regla: 'Spread política', ok: controles.spread_politica, criterio: `>= ${(spreadMin * 100).toFixed(2)}%`, valor: spreadPolitica },
    { regla: 'Clasificación flujo automático (todas)', ok: controles.clasificacion_flujo, criterio: 'Automático', valor: clasResumen.flujo },
  ];

  const reglas_aplicadas = armarReglasAplicadas({
    lineas, cabecera: quote, params: p, notif, costoOp, costoOpLabel,
    contratoPolitica, contratoReal, costoContratoInternoPesos,
    cesion, costoFondoTotal: costoFondoPesos, pre, controles, concentracion,
    montoOperacionTotal, plazoPonderado,
    tasaReal: cabecera.tasa_operacion_real, tasaBase: cabecera.tasa_objetivo_base,
  });

  return {
    alertas,
    lineas,
    totales: {
      monto_factura: montoFacturaTotal,
      monto_operacion: montoOperacionTotal,
      cantidad_documentos: documentos.length,
      cantidad_deudores: cantidadDeudores,
      concentracion_deudor: concentracion,
      plazo_ponderado: plazoPonderado,
    },
    reglas_aplicadas,
    notificacion_deudor: notif,
    clasificacion: {
      codigo: clasResumen.codigo,
      banda: clasResumen.banda,
      ajuste_tasa_mensual: clasResumen.ajuste_tasa_mensual,
      flujo: clasResumen.flujo,
      tasa_objetivo_final: clasResumen.tasa_objetivo_final,
    },
    real: {
      interes: interesReal,
      comision: comisionReal,
      gastos: gastosReal,
      contrato: contratoReal,
      ingreso_bruto: ingresoBrutoReal,
      descuento_aplicado: cabecera.descuento_real,
      ingreso_neto: ingresoNetoReal,
      monto_liquido: montoLiquidoReal,
      spread: spreadReal,
      cumple_spread: spreadReal >= spreadMin,
      capacidad_descuento: capacidadDescuentoReal,
      margen_neto: margenReal,
      tasa_integral_neta: tasaIntReal,
    },
    politica: {
      interes: interesPolitica,
      comision: comisionPolitica,
      gastos: gastosPolitica,
      contrato: contratoPolitica,
      ingreso_bruto: ingresoBrutoPolitica,
      descuento_aplicado: descuentoAplicadoPolitica,
      ingreso_neto: ingresoNetoPolitica,
      monto_liquido: montoLiquidoPolitica,
      spread: spreadPolitica,
      cumple_spread: spreadPolitica >= spreadMin,
      capacidad_descuento: capacidadDescuentoPolitica,
      margen_neto: margenPolitica,
      tasa_integral_neta: tasaIntPolitica,
    },
    costos_internos: {
      costo_fondo: costoFondoPesos,
      costo_contrato: costoContratoInternoPesos,
      costo_operacion_fijo: costoOp,
      costo_notificacion: notif.total,
      costo_cesion: cesion,
      costo_operacional: costoOperacional,
      total: costoInternoTotal,
    },
    controles,
    pre_evaluacion: {
      resultado: cumplePreEval ? 'Aprobada preliminar' : 'Revision manual',
      cumple_todas: cumplePreEval,
      detalle: detallePreEval,
    },
    parametros_usados: p,
  };
}

export function clp(n) {
  return Math.round(Number(n) || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
}

export function pct(n) {
  return `${(Number(n) * 100).toFixed(3)}%`;
}

const docInicial = emptyDocumento({
  monto_factura: 1428000,
  monto_operacion: 1428000,
  plazo: 29,
  comision_real: 10000,
  gastos_real: 10000,
  clasificacion_aplicada: '',
});

export const initialQuote = {
  tipo_cliente: 'Antiguo',
  contrato_vigente: 'Si',
  tipo_contrato: 'No aplica',
  tasa_operacion_real: 0.017,
  tasa_objetivo_base: 0.017,
  contrato_real: 0,
  descuento_real: 0,
  descuento_campana: 0,
  descuento_ejecutivo: 0,
  rut_cliente: '',
  nombre_cliente: '',
  documentos: [docInicial],
  monto_factura: 1428000,
  monto_operacion: 1428000,
  plazo: 29,
  comision_real: 10000,
  gastos_real: 10000,
  cantidad_deudores: 1,
  concentracion_deudor: 1428000,
  rut_deudor: '',
  clasificacion_bd: '',
  clasificacion_aplicada: '',
  clasificacion_manual: false,
};
